import React, { type ReactNode } from 'react';
import EmptyState from '@atlaskit/empty-state';
import { Text, Box, Inline, xcss } from '@atlaskit/primitives';
import HelpPanelLink from '@atlassian/jira-help-panel-button/src/index.tsx';
import SearchErrorIllustration from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/search-error/index.tsx';
import StormIllustration from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/components/storm/index.tsx';
import DependenciesGraphIllustration from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/dependency-graph/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { IPH_IDS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant.tsx';
import DocLink from '@atlassian/jira-portfolio-3-portfolio/src/common/view/doc-link/index.tsx';
import messages from './messages.tsx';
import { ScreenType, type Props } from './types.tsx';

export default function StateScreen({ type }: Props) {
	const { formatMessage } = useIntl();

	let IllustrationComponent: typeof SearchErrorIllustration;
	let title = '';
	let text;
	let primaryAction;
	switch (type) {
		case ScreenType.WARNING:
			IllustrationComponent = StormIllustration;
			title = formatMessage(messages.warningTitle);
			text = formatMessage(messages.warningText);
			break;
		case ScreenType.EMPTY_NO_DEPENDENCY:
			IllustrationComponent = DependenciesGraphIllustration;
			title = formatMessage(messages.noDependencyTitle);
			text = formatMessage(messages.noDependencyText, {
				strong: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
			});
			primaryAction = (
				<HelpPanelLink
					articleId={IPH_IDS.DEPENDENCIES_REPORT}
					fallbackComponent={
						<DocLink href={DocLink.URLs.dependencyReport}>
							{formatMessage(messages.iphLink)}
						</DocLink>
					}
					appearance="link"
					spacing="none"
				>
					{formatMessage(messages.iphLink)}
				</HelpPanelLink>
			);

			break;
		case ScreenType.EMPTY_TOO_MANY_FILTERS:
			IllustrationComponent = SearchErrorIllustration;
			title = formatMessage(messages.tooManyFiltersTitle);
			text = formatMessage(messages.tooManyFiltersText);
			break;
		default:
			break;
	}
	return (
		<EmptyState
			renderImage={() => (
				<Inline alignInline="center">
					<Box xcss={illustrationStyles}>
						<IllustrationComponent fill alt="" />
					</Box>
				</Inline>
			)}
			header={title}
			description={text}
			primaryAction={primaryAction}
		/>
	);
}

const illustrationStyles = xcss({
	position: 'relative',
	height: '160px',
	width: '230px',
	marginBottom: 'space.300',
});
